/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { assignmentApi } from '../services/assignmentApi';
import AssignedTerminal from '../components/assignments/AssignedTerminal';
import PodLogStream from '../components/assignments/PodLogStream';

const AssignedHelmDetail: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [openTerm, setOpenTerm] = useState<{ pod: string; container?: string } | null>(null);
  const [openLogs, setOpenLogs] = useState<{ pod: string; container?: string } | null>(null);

  const detail = useQuery({
    queryKey: ['assigned-helm', id],
    queryFn: () => assignmentApi.getHelm(id),
    refetchInterval: 15000,
    enabled: !!id,
  });

  const uninstall = useMutation({
    mutationFn: () => assignmentApi.uninstallHelm(id),
    onSuccess: () => {
      toast.success('Helm release uninstalled and assignment removed.');
      qc.invalidateQueries({ queryKey: ['my-assigned-helm'] });
      nav('/assignments?tab=helm');
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error || err?.message || 'Uninstall failed'),
  });

  const row = detail.data?.data;
  const live = detail.data?.live;
  const pods = detail.data?.pods || [];
  const isActive = row?.status === 'active';

  if (detail.isLoading) {
    return (
      <div className="p-6">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  if (detail.isError || !row) {
    return (
      <div className="p-6">
        <div className="text-red-400">Failed to load helm release assignment.</div>
        <Link to="/assignments?tab=helm" className="text-primary-500 underline mt-2 block">
          Back to Assigned Resources
        </Link>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Back link */}
      <Link
        to="/assignments?tab=helm"
        className="flex items-center gap-1 text-sm text-gray-400 hover:text-white mb-4"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Assigned Resources
      </Link>

      <h1 className="text-2xl font-bold text-white mb-2">{row.display_name}</h1>

      {/* Banners */}
      {row.status === 'suspended' && (
        <div className="mb-4 bg-yellow-900/30 border border-yellow-700/50 text-yellow-400 p-3 rounded-lg text-sm">
          This helm release is suspended due to low wallet balance. Actions are disabled until
          reactivated.{' '}
          <Link to="/billing/payments?action=topup" className="underline">
            Top up wallet
          </Link>
          .
        </div>
      )}
      {row.status === 'removed' && (
        <div className="mb-4 bg-gray-800 border border-gray-700 text-gray-400 p-3 rounded-lg text-sm">
          This assignment has been removed by an administrator.
        </div>
      )}

      {/* Release details */}
      <div className="space-y-2 text-sm mb-6 border border-gray-700 rounded-lg p-4 bg-gray-900">
        <div className="flex gap-2">
          <span className="text-gray-400 w-36">Release name:</span>
          <span className="text-white">{row.release_name}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-400 w-36">Chart:</span>
          <span className="text-white">
            {row.chart_name}@{row.chart_version}
          </span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-400 w-36">Namespace:</span>
          <span className="text-white">{row.namespace}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-400 w-36">Helm status:</span>
          <span className="text-white">{live?.status || '-'}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-400 w-36">Monthly cost:</span>
          <span className="text-white">₹{row.monthly_cost.toFixed(2)}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-400 w-36">Assignment status:</span>
          <span className="text-white capitalize">{row.status}</span>
        </div>
        <div className="flex gap-2">
          <span className="text-gray-400 w-36">Assigned at:</span>
          <span className="text-white">{new Date(row.assigned_at).toLocaleString()}</span>
        </div>
        {row.notes && (
          <div className="flex gap-2">
            <span className="text-gray-400 w-36">Notes:</span>
            <span className="text-white">{row.notes}</span>
          </div>
        )}
      </div>

      {/* Pods table */}
      <h2 className="text-lg font-semibold text-white mb-3">Pods in this release</h2>
      {pods.length === 0 ? (
        <div className="text-gray-400 text-sm mb-6">No pods found for this release.</div>
      ) : (
        <div className="border border-gray-700 rounded-lg overflow-hidden mb-6">
          <table className="w-full text-sm">
            <thead className="bg-gray-800">
              <tr>
                <th className="text-left px-4 py-3 text-gray-300 font-medium">Pod</th>
                <th className="text-left px-4 py-3 text-gray-300 font-medium">Phase</th>
                <th className="text-left px-4 py-3 text-gray-300 font-medium">Containers</th>
                <th className="px-4 py-3 text-gray-300 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pods.map((p) => (
                <tr key={p.name} className="border-t border-gray-700">
                  <td className="px-4 py-3 text-white">{p.name}</td>
                  <td className="px-4 py-3 text-gray-300">{p.phase}</td>
                  <td className="px-4 py-3 text-gray-400 text-xs">
                    {p.containers.join(', ')}
                  </td>
                  <td className="px-4 py-3 text-right space-x-3">
                    <button
                      className="text-primary-400 hover:text-primary-300 text-xs underline"
                      disabled={!isActive}
                      onClick={() => {
                        const container = p.containers[0];
                        setOpenLogs({ pod: p.name, container });
                        setOpenTerm(null);
                      }}
                    >
                      Logs
                    </button>
                    <button
                      className="text-primary-400 hover:text-primary-300 text-xs underline disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={!isActive}
                      onClick={() => {
                        const container = p.containers[0];
                        setOpenTerm({ pod: p.name, container });
                        setOpenLogs(null);
                      }}
                    >
                      Terminal
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Inline log stream */}
      {openLogs && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-white text-sm">
              Logs: {openLogs.pod}
              {openLogs.container ? ` (${openLogs.container})` : ''}
            </h3>
            <button
              className="text-xs text-gray-400 hover:text-white border border-gray-700 rounded px-2 py-1"
              onClick={() => setOpenLogs(null)}
            >
              Close
            </button>
          </div>
          <PodLogStream
            wsUrl={assignmentApi.helmLogsWsURL(id, openLogs.pod, openLogs.container)}
          />
        </div>
      )}

      {/* Inline terminal */}
      {openTerm && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-white text-sm">
              Terminal: {openTerm.pod}
              {openTerm.container ? ` (${openTerm.container})` : ''}
            </h3>
            <button
              className="text-xs text-gray-400 hover:text-white border border-gray-700 rounded px-2 py-1"
              onClick={() => setOpenTerm(null)}
            >
              Close
            </button>
          </div>
          <AssignedTerminal
            wsUrl={assignmentApi.helmTerminalWsURL(id, openTerm.pod, openTerm.container)}
            title={openTerm.pod}
          />
        </div>
      )}

      {/* Uninstall section */}
      <div className="border border-red-900/50 rounded-lg p-4 bg-red-950/20">
        <h3 className="font-semibold text-red-400 mb-1">Uninstall Release</h3>
        <p className="text-sm text-gray-400 mb-3">
          Runs <code className="bg-gray-800 px-1 rounded text-red-300">helm uninstall {row.release_name}</code>.
          All pods, services, and volumes in this release will be permanently deleted. This cannot be
          undone.
        </p>
        <button
          disabled={!isActive || uninstall.isPending}
          onClick={() => {
            if (
              window.confirm(
                `Uninstall release "${row.release_name}" and remove this assignment? This cannot be undone.`
              )
            ) {
              uninstall.mutate();
            }
          }}
          className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {uninstall.isPending ? 'Uninstalling...' : 'Uninstall Release'}
        </button>
        {!isActive && (
          <p className="text-xs text-gray-500 mt-2">
            Release is {row.status} - actions disabled.
          </p>
        )}
      </div>
    </div>
  );
};

export default AssignedHelmDetail;
